import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EagleLogo } from "@/components/EagleLogo";
import { Lock, CheckCircle2 } from "lucide-react";
import { api, describeRequestError } from "@/lib/api";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
        if (password !== confirm) { setErr("Passwords do not match."); return; }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setDone(true);
        } catch (e2) {
            setErr(describeRequestError(e2, "Could not reset password."));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center paper-bg px-4" data-testid="reset-password-page">
            <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-8 max-w-md w-full">
                <EagleLogo className="h-12 w-12" color="#3A2418" />
                <h2 className="font-bold text-2xl mt-3 text-brand-charcoal">Set your password</h2>

                {!token && (
                    <p className="text-sm text-brand-danger mt-3" data-testid="reset-no-token">This link is missing its token. Please use the link from your email.</p>
                )}

                {done ? (
                    <div className="mt-5" data-testid="reset-success">
                        <div className="flex items-center gap-2 text-brand-success bg-brand-success/10 border border-brand-success/30 rounded-xl px-4 py-3">
                            <CheckCircle2 className="h-5 w-5" />
                            <span className="text-sm font-semibold">Password updated. You can now sign in.</span>
                        </div>
                        <button onClick={() => navigate("/login", { replace: true })} className="mt-5 w-full h-12 bg-brand-navy hover:bg-brand-navy-dark text-brand-bone rounded-xl font-bold" data-testid="go-to-login-button">
                            Go to sign in
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-5 space-y-3" data-testid="reset-password-form">
                        {err && <div className="text-sm text-brand-danger bg-brand-danger/10 border border-brand-danger/30 rounded-lg px-3 py-2" data-testid="reset-error">{err}</div>}
                        <div className="relative">
                            <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/40" />
                            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 8 chars)" className="w-full h-12 pl-10 pr-3 border border-brand-charcoal/20 rounded-xl" data-testid="reset-new-password" />
                        </div>
                        <div className="relative">
                            <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/40" />
                            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className="w-full h-12 pl-10 pr-3 border border-brand-charcoal/20 rounded-xl" data-testid="reset-confirm-password" />
                        </div>
                        <button type="submit" disabled={busy || !token} className="w-full h-12 bg-brand-navy hover:bg-brand-navy-dark text-brand-bone rounded-xl font-bold disabled:opacity-60" data-testid="reset-submit">
                            {busy ? "Saving…" : "Set password"}
                        </button>
                        <button type="button" onClick={() => navigate("/login")} className="w-full text-center text-sm text-brand-navy underline underline-offset-4" data-testid="reset-back-to-login">
                            Back to sign in
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
